import { formatCredits, formatFairUseTimeframe } from "@app/lib/client/credits";
import type {
  MaxAwuCreditsTimeframeType,
  MaxMessagesTimeframeType,
  PlanType,
} from "@app/types/plan";
import { assertNeverAndIgnore } from "@app/types/shared/utils/assert_never";
import {
  Attachment01,
  Icon,
  Markdown,
  Sheet,
  SheetContainer,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@dust-tt/sparkle";
import i18n from "i18next";
import { useTranslation } from "react-i18next";

// The per-seat fair-use limit for a plan: if the plan sets maxAwuCredits it's a
// credit limit, otherwise it's a max number of messages.
export type FairUseSeatLimit =
  | { kind: "credits"; limit: number; timeframe: MaxAwuCreditsTimeframeType }
  | { kind: "messages"; limit: number; timeframe: MaxMessagesTimeframeType };

// If the plan has maxAwuCredits, it's an AWU credit limit; otherwise it's a max
// number of messages. Undefined when neither is set (both are the -1 sentinel).
export function fairUseSeatLimitFromPlan(
  plan: PlanType
): FairUseSeatLimit | undefined {
  const {
    maxAwuCredits,
    maxAwuCreditsTimeframe,
    maxMessages,
    maxMessagesTimeframe,
  } = plan.limits.assistant;

  if (maxAwuCredits !== -1) {
    return {
      kind: "credits",
      limit: maxAwuCredits,
      timeframe: maxAwuCreditsTimeframe,
    };
  }
  if (maxMessages !== -1) {
    return {
      kind: "messages",
      limit: maxMessages,
      timeframe: maxMessagesTimeframe,
    };
  }
  return undefined;
}

interface FairUsageModalProps {
  isOpened: boolean;
  onClose: () => void;
  // The fair-use limit for the current plan, when known. Omitted on generic
  // surfaces (e.g. the pricing page) where no single plan is in context.
  seatLimit?: FairUseSeatLimit;
}

function getFairUseContent(seatLimit?: FairUseSeatLimit): string {
  let limitLine: string;
  switch (seatLimit?.kind) {
    case "credits": {
      const timeframeLabel = formatFairUseTimeframe(seatLimit.timeframe);
      limitLine = i18n.t(
        "misc.fairUseLimitCreditsLine",
        "On your current plan, that is **{{credits}} credits{{timeframe}}**.",
        {
          credits: formatCredits(seatLimit.limit),
          timeframe: timeframeLabel ? ` ${timeframeLabel}` : "",
        }
      );
      break;
    }
    case "messages": {
      const timeframeLabel = formatFairUseTimeframe(seatLimit.timeframe);
      limitLine = i18n.t(
        "misc.fairUseLimitMessagesLine",
        "On your current plan, that is **{{limit}} messages{{timeframe}}**.",
        {
          limit: seatLimit.limit,
          timeframe: timeframeLabel ? ` ${timeframeLabel}` : "",
        }
      );
      break;
    }
    case undefined:
      limitLine = i18n.t(
        "misc.fairUseLimitUnknownLine",
        "The exact limit depends on your plan."
      );
      break;
    default:
      assertNeverAndIgnore(seatLimit);
      limitLine = i18n.t(
        "misc.fairUseLimitUnknownLine",
        "The exact limit depends on your plan."
      );
  }

  // TODO(rebrand): relink "Programmatic usage at Dasis" when Dasis docs exist.
  return i18n.t(
    "misc.fairUsageModalBody",
    `
# **Fair use principles for user seats**

Each user seat at Dasis is tied to a specific human user, and is destined to be used by that person only, for the purposes of typing and sending messages manually (as opposed to using programmatic methods such as scripts, API calls, etc. which is covered separately).

To prevent abuse, a "fair use" limit applies to each user seat. {{limitLine}}

This limit should be understood as a way to prevent abuse, not as an allowed quota. In particular, it is considered unfair to share a single seat between multiple people.

___
# **Can messages be sent programmatically with Dasis?**

Yes, and this usage is encouraged. However, such messages are not covered by individual user seats and fair use limits, and are billed separately.

Dasis plans already include monthly credits for programmatic usage, and more credits can be purchased if needed, see Programmatic usage at Dasis.

`,
    { limitLine }
  );
}

export function FairUsageModal({
  isOpened,
  onClose,
  seatLimit,
}: FairUsageModalProps) {
  const { t } = useTranslation();
  return (
    <Sheet
      open={isOpened}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <SheetContent>
        <SheetHeader>
          <SheetTitle>
            {t("misc.fairUsageModalTitle", "Dasis's Fair Use Policy")}
          </SheetTitle>
        </SheetHeader>
        <SheetContainer>
          <Icon visual={Attachment01} size="lg" className="text-success-500" />
          <Markdown
            content={getFairUseContent(seatLimit)}
            forcedTextSize="text-sm"
          />
        </SheetContainer>
      </SheetContent>
    </Sheet>
  );
}
